import React, {useState} from "react";
import Grid from "./Grid";

const CloseCashBookTable = ({ data, isAdmin, onApprove, onReject, pendingCount  }) => {

    const columns = [
        { field: "cash_book_code", headerName: "CashBook", flex: 1 },

        {
            field: "location",
            headerName: "Location",
            flex: 1,
            renderCell: (params) => {
                const loc = params.row.location;

                if (!loc) {
                    return (
                        <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded text-xs">
                            🏢 Organization
                        </span>
                    );
                }

                const type = loc.location_type;

                const iconMap = {
                    Farm: "🌾",
                    Outlet: "🏪"
                };

                const colorMap = {
                    Farm: "bg-green-100 text-green-700",
                    Outlet: "bg-blue-100 text-blue-700"
                };

                return (
                    <div className="flex flex-col">
                        <span className="font-medium text-gray-800">
                            {iconMap[type] || "📍"} {loc.location_name}
                        </span>

                        <span className={`text-xs px-2 py-0.5 rounded w-fit ${colorMap[type] || "bg-gray-100 text-gray-600"}`}>
                            {type || "Location"}
                        </span>
                    </div>
                );

            }
        },

        {
            field: "requested_by",
            headerName: "Requested By",
            flex: 1,
            renderCell: p => "👤 " + (p.row.closure_requested_by?.username || "-")
        },

        {
            field: "balance",
            headerName: "Balance",
            flex: 1,
            renderCell: p => {
                const balance = p.row.current_balance ?? 0;

                return balance < 0 ? (
                    <span className="text-red-600 font-semibold">
                        🔻 Rs {balance}
                    </span>
                ) : (
                    <span className="text-green-600 font-semibold">
                        🔺 Rs {balance}
                    </span>
                );
            }
        },

        {
            field: "status",
            headerName: "Status",
            flex: 1,
            renderCell: (p) => {
                const status = p.row.status;

                const color =
                    status === "ClosedPendingApproval"
                        ? "bg-purple-600"
                        : status === "Closed"
                            ? "bg-red-600"  : "bg-gray-500";

                return (
                    <span className={`${color} text-white px-2 py-1 rounded text-xs`}>
                        {status === "ClosedPendingApproval" ? "Pending" : status}
                    </span>
                );
            }
        },

        ...(isAdmin ? [{
            field: "actions",
            headerName: "Actions",
            flex: 1,
            renderCell: (p) => {
                const status = p.row.status;

                if (status !== "ClosedPendingApproval") {
                    return null;
                }

                return (
                    <div className="flex items-center justify-center w-full h-full gap-2">

                        <button
                            onClick={() => onApprove(p.row._id)}
                            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs shadow"
                        >
                            Approve
                        </button>

                        <button
                            onClick={() => onReject(p.row._id)}
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs shadow"
                        >
                            Reject
                        </button>

                    </div>
                );
            }
        }] : [])
    ];

    const locationColorMap = {};

    const colors = [
        "bg-green-50",
        "bg-blue-50",
        "bg-yellow-50",
        "bg-purple-50"
    ];

    const borderColors = [
        "border-green-500",
        "border-blue-500",
        "border-yellow-500",
        "border-purple-500"
    ];

    data.forEach(req => {
        const key = req.location?._id || "-";

        if (!locationColorMap[key]) {
            const hash = key
                .split("")
                .reduce((acc, char) => acc + char.charCodeAt(0), 0);

            locationColorMap[key] = colors[hash % colors.length];
        }
    });

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mt-6 overflow-hidden">

            <div className="flex items-center justify-between px-4 py-3 border-b">

                <h1 className="text-xl font-bold text-red-600 mb-3 flex items-center gap-2">
                     Close CashBook Requests
                </h1>

                {pendingCount > 0 && isAdmin && (
                    <span className="flex items-center gap-1 bg-red-100 text-red-600 px-3 py-1 rounded-full text-xs font-medium">
                        ⛔ {pendingCount}
                    </span>
                )}

            </div>

            {data.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                    No pending close requests
                </div>
            ) : (
                <div className="p-2">
                    <Grid
                        rows={data}
                        columns={columns}
                        getRowId={(row) => row._id}
                        rowClassNameFn={(params) => {
                            const key = params.row.location?._id || "-";

                            const hash = key
                                .split("")
                                .reduce((acc, char) => acc + char.charCodeAt(0), 0);

                            const bg = locationColorMap[key] || "bg-gray-50";
                            const border = borderColors[hash % borderColors.length];

                            return `${bg} border-l-4 ${border}`;
                        }}
                    />
                </div>
            )}

        </div>
    );
};

export default CloseCashBookTable;